import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Check, ChevronLeft, Compass, Phone, X } from 'lucide-react';
import { cn } from 'lib/utils';
import { Button } from 'components/ui/button';
import { ApiError } from 'utils/unwrapResponse';
import { apiErrorMessage } from 'utils/errorMessage';
import { formatDateTime } from 'utils/formatDate';
import { formatDuration, formatElapsed } from 'utils/elapsed';
import {
  interventionDay,
  refusalReasonLabel,
} from 'pages/AdminIssues/components/upcomingAssignment';
import { calendarDay } from 'utils/interventionDate';
import DeferredSection from 'pages/AdminTechnician/DeferredSection';
import InfoRow from 'components/InfoRow';
import {
  useAcceptMyInterventionRequest,
  useDeclineMyInterventionRequest,
} from 'hooks/useMyInterventionRequests';
import type { MyInterventionRequest } from 'api/myInterventionRequestsApi';
import type {
  InterventionRequest,
  InterventionRequestStatus,
  RefusalReasonCode,
} from 'api/interventionRequestsApi';
import DeclineSheet from './DeclineSheet';
import { statusTint } from './requestVisuals';

/**
 * One request, and the two answers. The card constants and the status pill live here rather than
 * in a file of their own because the list imports them from this module — the other direction
 * would be a cycle, and the lot is deliberately four files.
 */

export const CARD = 'rounded-2xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900';
/** 44 px, the smallest target a thumb hits reliably — the mock-up draws 38. */
const TAP = 'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl';
export const HINT = 'text-sm leading-relaxed text-zinc-500 dark:text-zinc-400';
/** An em dash and never an empty line: a blank value would read as "not applicable". */
export const DASH = '—';

/**
 * His own words, and not the back office's: « En attente de réponse » describes somebody waiting
 * on him, which is not what he reads on his own request. A `switch` of literal keys — a key built
 * by concatenation is invisible to the parity check.
 */
const statusLabel = (t: TFunction, status: InterventionRequestStatus): string => {
  switch (status) {
    case 'accepted':
      return t('Accepted');
    case 'declined':
      return t('Declined');
    default:
      return t('New request');
  }
};

/** One pill for the list and the detail — the mock-up's tints, dot included. */
export const StatusPill = ({ status }: { status: InterventionRequestStatus }) => {
  const { t } = useTranslation();
  const tint = statusTint(status);
  return (
    <span
      className={cn(
        'inline-flex h-5 shrink-0 items-center gap-[5px] rounded-full pl-[7px] pr-2 text-[11px] font-medium',
        tint.bg,
        tint.fg
      )}
    >
      <span className={cn('h-[5px] w-[5px] shrink-0 rounded-full', tint.dot)} aria-hidden="true" />
      {statusLabel(t, status)}
    </span>
  );
};

/**
 * Why an answer failed — three families, **never collapsed into one** (D32).
 *
 * The status is read **before** the error code: a 401 rendered as « vous avez déjà répondu » would
 * make him believe his answer went out (R-053). The two 409 codes then keep their own sentences —
 * "already answered" and "given to somebody else" are not the same event (R-050) — and a thrown
 * value that is not an `ApiError` never reached the server at all, so it says so and stays
 * retryable.
 */
const answerError = (t: TFunction, error: unknown): string => {
  if (!(error instanceof ApiError)) {
    return t('The answer could not be sent. Check your connection, then try again.');
  }
  if (error.status === 401 || error.status === 403) {
    return t('Your session has expired. Sign in again, then answer.');
  }
  if (error.status === 404) return t('This request is no longer available.');
  if (error.errorCode) return apiErrorMessage(t, error);
  return t('The answer could not be sent. Try again in a moment.');
};

/**
 * His other missions **already accepted** on the same calendar day (D68) — pure arithmetic on the
 * list the screen already holds, so the day's load costs no query. `calendarDay` and never a
 * `slice`: a day that does not exist is « no date » here as everywhere (D65). Lives beside the
 * card like the constants above — the list is its only caller and already imports from here.
 */
export const acceptedSameDay = (
  requests: MyInterventionRequest[],
  current: MyInterventionRequest
): number => {
  const day = calendarDay(current.interventionDate);
  if (!day) return 0;
  return requests.filter(
    (other) =>
      other.id !== current.id &&
      other.status === 'accepted' &&
      calendarDay(other.interventionDate) === day
  ).length;
};

interface RequestDetailProps {
  request: MyInterventionRequest;
  /** Missions already accepted that same day, this one excluded — counted by the list, the only
   *  holder of his other requests. `0` is silent, and so is a request with no date. */
  sameDayMissions: number;
  /** The chevron and the pending footer: back to the list, filter untouched. */
  onBack: () => void;
  /** After an answer: back to the list, chip filter back to « Toutes ». */
  onDone: () => void;
}

const RequestDetail = ({ request, sameDayMissions, onBack, onDone }: RequestDetailProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const accept = useAcceptMyInterventionRequest();
  const decline = useDeclineMyInterventionRequest();

  const [declining, setDeclining] = useState(false);
  /** The row the server sent back, which is what carries the delay it measured. */
  const [answered, setAnswered] = useState<InterventionRequest | null>(null);

  const day = interventionDay(request.interventionDate);
  const pending = request.status === 'pending';
  const busy = accept.isPending || decline.isPending;

  const confirmDecline = (refusalReasonCode: RefusalReasonCode, refusalReason: string | null) => {
    if (busy) return;
    decline.mutate(
      { id: request.id, refusalReasonCode, refusalReason },
      {
        onSuccess: (updated) => {
          setDeclining(false);
          setAnswered(updated);
        },
      }
    );
  };

  // Success is a screen, not a silence: what he answered, on which mission, and how long he took.
  if (answered) {
    return (
      <div className="flex flex-col items-center gap-3 py-10 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400">
          <Check className="h-6 w-6" aria-hidden="true" />
        </span>
        <p className="text-[15px] font-semibold text-zinc-900 dark:text-zinc-50">
          {answered.status === 'accepted' ? t('Request accepted') : t('Refusal recorded')}
        </p>
        <p className={cn('max-w-[300px]', HINT)}>
          {[request.store || DASH, request.requestType, day].filter(Boolean).join(' · ')}
        </p>
        {answered.responseDelayS !== null && (
          <p className="text-xs text-zinc-400 dark:text-zinc-500">
            {t('Answered in {{duration}}', { duration: formatDuration(answered.responseDelayS) })}
          </p>
        )}
        <Button className="mt-4 h-12 w-full max-w-sm text-sm" onClick={onDone}>
          {t('Back to my requests')}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 pb-4">
      <div className="flex items-center gap-1 border-b border-zinc-200 pb-3 dark:border-zinc-800">
        {/* Kept because it works in a browser — and never the only way out: on iOS the WebView's
            back-forward gesture swallows every touch within ~50 pt of the left edge, which is
            where this chevron sits. The footer below carries the exit that survives that (D33). */}
        <button
          type="button"
          onClick={onBack}
          aria-label={t('Back')}
          className={cn(TAP, '-ml-2 text-zinc-900 dark:text-zinc-100')}
        >
          <ChevronLeft className="h-6 w-6" aria-hidden="true" />
        </button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-base font-semibold text-zinc-900 dark:text-zinc-50">
            {[request.store || DASH, request.storeCity].filter(Boolean).join(' · ')}
          </p>
          <p className="truncate font-mono text-xs text-zinc-500 dark:text-zinc-400">
            {request.issueKey}
          </p>
        </div>
        <StatusPill status={request.status} />
      </div>

      <section className={cn(CARD, 'p-4')}>
        <p className="text-[15px] font-semibold text-zinc-900 dark:text-zinc-50">
          {request.store || DASH}
        </p>
        <p className="mt-0.5 text-[13px] text-zinc-500 dark:text-zinc-400">
          {[request.storeCity, request.requestType].filter(Boolean).join(' · ') || DASH}
        </p>
        <p className="mt-2 text-xs text-zinc-400 dark:text-zinc-500">
          {t('Received {{when}}', { when: formatElapsed(request.elapsedSeconds) })}
          {request.serviceDeskName ? ` · ${request.serviceDeskName}` : ''}
        </p>
      </section>

      {/* No « Créneau » row: nothing in the system carries a time slot (D11/D15), and the day is
          the only thing the desk's own field holds. Never a time. */}
      <section className={cn(CARD, 'overflow-hidden')}>
        <h2 className="px-4 pb-3 pt-4 text-sm font-semibold text-zinc-900 dark:text-zinc-50">
          {t('Intervention')}
        </h2>
        <InfoRow label={t('Client')} value={request.client || DASH} />
        <InfoRow label={t('Point of sale')} value={request.store || DASH} />
        <InfoRow label={t('City')} value={request.storeCity || DASH} />
        <InfoRow label={t('Address')} value={request.storeAddress || DASH} />
        <InfoRow label={t('Request type')} value={request.requestType || DASH} />
        <InfoRow label={t('Date')} value={day || DASH} />
      </section>

      <section className={cn(CARD, 'p-4')}>
        <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">{t('Description')}</h2>
        <p className={cn('mt-2 whitespace-pre-line', HINT)}>{request.description || DASH}</p>
      </section>

      <section className={cn(CARD, 'p-4')}>
        <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">
          {t('Useful information')}
        </h2>
        {/* `customMessage` is provisioned and no screen writes it yet, so the fall-back is the
            normal case — and saying so is honest rather than a placeholder. */}
        <p className={cn('mt-2', HINT)}>
          {request.customMessage || t('No extra information was added to this request.')}
        </p>
      </section>

      {/* Not a silent `—`: measured, **zero** of 4483 payloads carry a phone, and the only
          Postgres candidate is a logistics contact. A dash would read "this store has no phone"
          when the truth is that nobody has mapped one (R-045). */}
      <DeferredSection
        icon={Phone}
        title={t('Store phone')}
        explanation={t(
          'No phone number is mapped for this store yet: the ticket carries none, and the only number we hold is a logistics contact. Ask the back office if you need to call ahead.'
        )}
      />

      {/* He cannot guess it, and it is a legitimate ground for refusing (D22). */}
      {request.sentOutsidePerimeter && (
        <p className="flex gap-2.5 rounded-xl border border-amber-200 bg-amber-50 p-3 text-[13px] leading-relaxed text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300">
          <Compass className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {t(
            'This mission is outside the area you usually cover. You may decline for that reason.'
          )}
        </p>
      )}

      {!pending && (
        <section className={cn(CARD, 'p-4')}>
          <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">
            {t('Your answer')}
          </h2>
          <p className="mt-2 text-[13px] font-medium text-zinc-900 dark:text-zinc-50">
            {request.status === 'accepted' ? t('You accepted this request.') : t('You declined this request.')}
          </p>
          {request.status === 'declined' && request.refusalReasonCode && (
            <p className={cn('mt-1', HINT)}>{refusalReasonLabel(t, request.refusalReasonCode)}</p>
          )}
          {request.refusalReason && <p className={cn('mt-1', HINT)}>{request.refusalReason}</p>}
          <p className="mt-2 text-xs text-zinc-400 dark:text-zinc-500">
            {[
              request.respondedAt ? formatDateTime(request.respondedAt) : null,
              request.responseDelayS !== null
                ? t('Answered in {{duration}}', { duration: formatDuration(request.responseDelayS) })
                : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
          {/* The lock is a `WHERE status = 'pending'`: an answer cannot be taken back, by
              construction. An unspoken dead end is a defect, so the card says it — and points at
              the one lever he does have, without promising a cancellation the model forbids
              (B-07: a declared unavailability never touches an accepted mission). */}
          <p className={cn('mt-3', HINT)}>{t('This answer is final and cannot be changed from the application.')}</p>
          {request.status === 'accepted' && (
            <>
              <p className={cn('mt-2', HINT)}>
                {t(
                  'If something prevents you from going, declare an unavailability from your profile. That does not cancel this intervention — the back office will contact you to reorganise it.'
                )}
              </p>
              <Button
                variant="outline"
                className="mt-3 h-11 w-full text-sm"
                onClick={() => navigate('/profile')}
              >
                {t('Open my profile')}
              </Button>
            </>
          )}
        </section>
      )}

      {/* The decline failure is rendered inside the sheet, where he is looking. This one is the
          acceptance's. */}
      {accept.error && (
        <p
          role="alert"
          className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-[13px] leading-relaxed text-rose-700 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-300"
        >
          {answerError(t, accept.error)}
        </p>
      )}

      {/* Sticky, and full-bleed over the page's own padding so nothing shows through under it. */}
      <div className="sticky bottom-0 -mx-4 flex flex-col gap-2.5 border-t border-zinc-200 bg-white px-4 pb-[30px] pt-3 dark:border-zinc-800 dark:bg-zinc-950">
        {/* What he has already taken that day, where he decides (D68) — informs, never blocks; the
            refusal already carries a « créneau » reason. `pending` only: after, it is decided. */}
        {pending && sameDayMissions > 0 && (
          <p className={cn(HINT, 'text-center')}>
            {t('You have already accepted {{count}} intervention that day', {
              count: sameDayMissions,
            })}
          </p>
        )}
        {pending && (
          <div className="flex gap-2.5">
            <Button
              variant="outline"
              className="h-12 flex-1 gap-2 rounded-xl border-rose-700 bg-white text-[14.5px] font-semibold text-rose-700 shadow-none hover:bg-rose-50 hover:text-rose-700 active:bg-rose-50 dark:border-rose-400 dark:bg-zinc-900 dark:text-rose-400 dark:hover:bg-rose-950 dark:hover:text-rose-400 dark:active:bg-rose-950 [&_svg]:size-[17px]"
              disabled={busy}
              onClick={() => setDeclining(true)}
            >
              <X aria-hidden="true" />
              {t('Decline')}
            </Button>
            <Button
              className="h-12 flex-1 gap-2 rounded-xl border border-primary text-[14.5px] font-semibold shadow-none [&_svg]:size-[18px]"
              disabled={busy}
              onClick={() => accept.mutate(request.id, { onSuccess: setAnswered })}
            >
              <Check aria-hidden="true" />
              {t('Accept')}
            </Button>
          </div>
        )}
        {/* Full width on purpose: the iOS dead zone is the leftmost ~50 pt, and a button that
            spans the screen is always tappable past it. Offered on `pending` too — the mock-up
            only draws it once answered, which leaves the new request with the dead chevron alone. */}
        <Button variant="ghost" className="h-11 w-full text-sm" onClick={pending ? onBack : onDone}>
          {t('Back to my requests')}
        </Button>
      </div>

      <DeclineSheet
        open={declining}
        pending={decline.isPending}
        error={decline.error ? answerError(t, decline.error) : null}
        onClose={() => setDeclining(false)}
        onConfirm={confirmDecline}
      />
    </div>
  );
};

export default RequestDetail;
